import { Component } from 'react';
import { Modal, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { inject, observer } from 'mobx-react';
import moment from 'moment/min/moment-with-locales';

// Components
import applicationRouter from '~/hoc/applicationRouter';
import withLocalization from '../../../hoc/withLocalization';
import DatePicker from '../../../components/DatePicker/DatePicker';
import { ProjectsWidget } from '../../../components/GenericForm/Widgets/ProjectsWidget';
import { TasksWidget } from '../../../components/GenericForm/Widgets/TasksWidget';
import RosterSchedulePreview from './RosterSchedulePreview.js';
import { AfterBulkCreateFailedResponseModalBody, BulkShiftSuccessSummary } from './AfterCopyResponse.js';

// Elements
import Button from '~/components/CustomButton/CustomButton.jsx';
import LoadingSpinner from '~/elements/LoadingSpinner';
import TimePickerInput from '../../../components/TimePicker';
import CheckboxButton from '../../../components/CustomButtonCheckbox/CustomButtonCheckbox';
import Checkbox from '../../../components/CustomCheckbox/CustomCheckbox';
import Select from '../../../elements/Select';
import RequiredStar from '../../../elements/RequiredStar.js';

// Assets
import CloseModalIcon from '../../../assets/img/CloseApproveModalIcon.svg';

// Utils
import { getMinutesFromTimeInput, minutesToTimeInput } from '../../../utils/timeCalc';
import { longDateFormat } from '../../../utils/dateFormat';
import { getCompanyShiftDetails } from '../../../utils/commonMethods.js';
import { AUTO_TIMESHEET_DAY_KEYS } from '../../../utils/constants';
import AutoTimeRecordingDays from '~/components/RosterShiftOptions/AutoTimeRecordingDays';
import { checkUserPermissions } from '../../../library/stores/userStore';

// Styles
import './Schedule.css';

class MemberBulkShiftForm extends Component {
    constructor(props) {
        super(props);
        const { commonStore, userStore } = props;
        const currentUser = userStore.currentUser;
        const companyShiftId = currentUser.company_shift_id || 0;
        const companyShiftDetails = getCompanyShiftDetails(commonStore.config, companyShiftId, minutesToTimeInput);

        this.companyShiftsOptions = commonStore.config.client?.allow_company_shifts && commonStore.config.companyShifts?.length
            ? commonStore.config.companyShifts.map(cs => ({ label: cs.shift_name, value: cs.id }))
            : null;

        // Members with only manageFutureScheduleEntry can only create future schedule entries here, which requires auto-schedule cron mode.
        const isAutoScheduleMode = commonStore.config?.client?.data?.loginRules?.autoTimelogs === 'autoschedule';
        this.isFutureEntryOnlyMode = checkUserPermissions('md_Shift', 'manageFutureScheduleEntry') && !checkUserPermissions('md_Shift', 'createEditAndDelete') && isAutoScheduleMode;

        // holds manageFutureScheduleEntry at all, regardless of their own profile auto-timesheet toggle
        this.hasFutureScheduleEntryPermission = !this.isFutureEntryOnlyMode
            && isAutoScheduleMode
            && checkUserPermissions('md_Shift', 'manageFutureScheduleEntry');

        const allowUserAutoTimesheetToggle = !!commonStore?.config?.client?.data?.loginRules?.allowUserToEnableDisableAutoTimesheet;
        this.isOwnAutoTimesheetProfileDisabled = allowUserAutoTimesheetToggle && currentUser.allow_user_for_auto_timesheet === false;
        this.hasFutureEntryDaysMode = this.hasFutureScheduleEntryPermission && this.isOwnAutoTimesheetProfileDisabled;

        this.canCreateFutureEntry = this.isFutureEntryOnlyMode || this.hasFutureScheduleEntryPermission;

        const startProp = new Date(props.viewModel.startDate);
        const endProp = new Date(props.viewModel.endDate);
        const now = new Date();

        this.state = {
            start_date: (startProp > now ? startProp : new Date(now.setDate(now.getDate() + 1))).toString(),
            end_date: (endProp > now ? endProp : new Date(now.setDate(now.getDate() + 6))).toString(),
            project_id: null,
            task_id: null,

            // Default shift fields (Path B — no roster)
            start_time: getMinutesFromTimeInput(companyShiftDetails.start),
            end_time: getMinutesFromTimeInput(companyShiftDetails.end),
            company_shift_id: companyShiftDetails.companyShiftId,
            defaultDays: [
                { day: 'Mon', checked: false }, { day: 'Tue', checked: false },
                { day: 'Wed', checked: false }, { day: 'Thu', checked: false },
                { day: 'Fri', checked: false }, { day: 'Sat', checked: false },
                { day: 'Sun', checked: false }, { day: 'Check All', checked: false },
            ],
            nonWorkingDays: [0, 1, 2, 3, 4, 5, 6],

            autoTimesheetDays: { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false },
            // which days become future entries (hasFutureEntryDaysMode only), opt-in
            futureTimeRecordingDays: { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false },

            // null | 'default' (profile shift) | 'roster' | 'no_roster' | 'choice' (roster + future entry, pick via entryChoice)
            formMode: null,
            entryChoice: 'roster', // 'roster' | 'default' — which option is selected while formMode === 'choice'
            isCheckingRoster: false,
            activeDatePicker: null, // 'from' | 'to' | null
            publishedTemplates: [],

            shiftsFailedToCreate: null,
            shiftsFailedToCreateMessage: null,
            shiftsCreatedSuccessfully: null,
            hasShiftCreated: false,
        };
    }

    componentDidMount() {
        this.checkRosterForDates(this.state.start_date, this.state.end_date);
    }

    checkRosterForDates = async (start_date, end_date) => {
        const { scheduleStore, commonStore, userStore } = this.props;
        const allowWorkHoursSettings = commonStore?.config?.client?.data?.basicRules?.workPlanSettings?.allow_work_hours_settings || false;
        const isNoDepartmentMember = userStore.currentUser.employee_type === 'no_department';
        // roster feature off, or a no_department member → always the default profile-shift form
        if (!allowWorkHoursSettings || isNoDepartmentMember) {
            this.setState({ formMode: 'default', isCheckingRoster: false });
            return;
        }
        // department member + roster on → only roster shifts (or "no roster found" for the period)
        this.setState({ isCheckingRoster: true, formMode: null });
        try {
            const { rosterTemplates } = await scheduleStore.getMemberRosterTemplates({
                start_date: moment(start_date).format('YYYY-MM-DD'),
                end_date: moment(end_date).format('YYYY-MM-DD'),
            });
            const hasRoster = !!(rosterTemplates && rosterTemplates.length > 0);
            const fallbackMode = this.canCreateFutureEntry ? 'default' : 'no_roster';
            const rosterFoundMode = this.hasFutureScheduleEntryPermission ? 'choice' : (this.isFutureEntryOnlyMode ? 'default' : 'roster');
            this.setState({ publishedTemplates: rosterTemplates || [], formMode: hasRoster ? rosterFoundMode : fallbackMode, entryChoice: 'roster', isCheckingRoster: false });
        } catch (err) {
            console.error(err);
            this.setState({ formMode: this.canCreateFutureEntry ? 'default' : 'no_roster', isCheckingRoster: false });
        }
    };

    handleDateChange = (name, value) => {
        let { start_date, end_date } = this.state;
        if (name === 'start_date') start_date = value.toString();
        else end_date = value.toString();

        if (moment(end_date).isBefore(start_date)) {
            if (name === 'start_date') end_date = start_date;
            else start_date = end_date;
        }

        this.setState({ start_date, end_date, activeDatePicker: null }, () => {
            this.checkRosterForDates(start_date, end_date);
        });
    };

    handleCheck = (index, evt) => {
        const { defaultDays, nonWorkingDays } = this.state;

        if (defaultDays[index].day === 'Check All') {
            const allDays = defaultDays.map(item => ({ ...item, checked: evt }));
            this.setState({
                defaultDays: allDays,
                nonWorkingDays: evt ? [] : [0, 1, 2, 3, 4, 5, 6],
            });
            return;
        }

        let holidays = [...nonWorkingDays];

        if (evt === false) {
            if (!holidays.includes(index)) holidays.push(index);
        } else {
            holidays = holidays.filter(e => e !== index);
        }

        let isAllTrue = true;
        const newDays = defaultDays.map((item, i) => i === index ? { ...item, checked: evt } : item);
        for (let i = 0; i < 7; i++) {
            if (!newDays[i].checked) { isAllTrue = false; break; }
        }
        newDays[7] = { ...newDays[7], checked: isAllTrue };

        this.setState({ defaultDays: newDays, nonWorkingDays: holidays });
    };

    getDisabledAutoTimesheetUserIds = () => {
        const { commonStore, userStore } = this.props;
        const allowUserAutoTimesheetToggle = !!commonStore?.config?.client?.data?.loginRules?.allowUserToEnableDisableAutoTimesheet;
        return allowUserAutoTimesheetToggle && userStore.currentUser.allow_user_for_auto_timesheet === false
            ? [userStore.currentUser.id]
            : [];
    };

    handleSaveRoster = async () => {
        const { start_date, end_date, project_id, task_id, autoTimesheetDays } = this.state;
        const { scheduleStore, commonStore, userStore, t } = this.props;
        if (!project_id || !task_id) {
            return commonStore.addNotification(t('Please select project and task'), null, 'warning');
        }
        const currentUserId = userStore.currentUser.id;
        const data = {
            start_date: moment(start_date).format('YYYY-MM-DD'),
            end_date: moment(end_date).format('YYYY-MM-DD'),
            project_id: parseInt(project_id),
            task_id: parseInt(task_id),
            assignees: [currentUserId],
            user_group: null,
            autoTimesheetDays,
            disabledAutoTimesheetUserIds: this.getDisabledAutoTimesheetUserIds(),
            isFutureScheduleEntry: this.isFutureEntryOnlyMode,
        };
        try {
            const res = await scheduleStore.addRosterShift(data);
            if (res?.shiftsFailedToCreate?.length) {
                const msg = res.draftShiftRecords?.length
                    ? t('Bulk shifts created, however some shifts failed to create please check')
                    : t('Failed to create shift due to intersections as shown below');
                commonStore.addNotification(msg, null, res.draftShiftRecords?.length ? 'warning' : 'error');
                this.setState({ shiftsFailedToCreate: res.shiftsFailedToCreate, shiftsFailedToCreateMessage: res.shiftsFailedToCreateMessage || null, hasShiftCreated: !!res.draftShiftRecords?.length });
            } else if (res?.draftShiftRecords?.length) {
                commonStore.addNotification(t('Shifts created successfully'), null, 'success');
                this.setState({
                    shiftsCreatedSuccessfully: { shifts: res.draftShiftRecords, usersMap: res.usersMap || {}, shiftTask: res.shiftTask || {} },
                    hasShiftCreated: true,
                });
            } else {
                commonStore.addNotification(t('No shifts were created for the selected period'), null, 'warning');
            }
        } catch (error) {
            commonStore.addNotification(t(error.message || 'Error'), null, 'error');
            console.error(error);
        }
    };

    handleSaveBulk = async () => {
        const { start_date, end_date, project_id, task_id, start_time, end_time, company_shift_id, nonWorkingDays, autoTimesheetDays, futureTimeRecordingDays, formMode, entryChoice } = this.state;
        const { scheduleStore, commonStore, userStore, t } = this.props;
        const isRosterChoiceFutureEntry = formMode === 'choice' && entryChoice === 'default';
        const rosterAppliesToMember = !!(commonStore?.config?.client?.data?.basicRules?.workPlanSettings?.allow_work_hours_settings)
            && userStore.currentUser.employee_type !== 'no_department';
        // 'default' as a roster-applies member only happens via the no-roster fallback - always forced future
        const isDepartmentFutureEntryOnly = this.hasFutureScheduleEntryPermission && formMode === 'default' && rosterAppliesToMember;
        const isForcedFutureEntryMode = isRosterChoiceFutureEntry || isDepartmentFutureEntryOnly;
        if (!project_id || !task_id) {
            return commonStore.addNotification(t('Please select project and task'), null, 'warning');
        }
        if (nonWorkingDays.length === 7) {
            return commonStore.addNotification(t('Please select days to create shift'), null, 'warning');
        }
        const startDateObj = new Date(start_date);
        const endDateObj = new Date(end_date);
        const startDate = new Date(Date.UTC(startDateObj.getUTCFullYear(), startDateObj.getUTCMonth(), startDateObj.getUTCDate()));
        const endDate = new Date(Date.UTC(endDateObj.getUTCFullYear(), endDateObj.getUTCMonth(), endDateObj.getUTCDate()));
        if (startDate.getTime() > endDate.getTime()) {
            return commonStore.addNotification(t('From date must be not bigger, than To date'), null, 'warning');
        }
        const currentUserId = userStore.currentUser.id;
        const values = {
            start_date,
            end_date,
            assignees: [currentUserId],
            assigneesBonus: [{ value: currentUserId, addBonus: false }],
            project_id,
            task_id,
            start_time,
            end_time,
            comment: '',
            do_notify: false,
            nonWorkingDays,
            user_group: null,
            isShiftAllowance: false,
            assignEmergencyShiftToEmployeeForTheWeek: false,
            company_shift_id,
            autoTimesheetDays,
            disabledAutoTimesheetUserIds: this.getDisabledAutoTimesheetUserIds(),
            autoAssignToProjectTask: true,
            isFutureScheduleEntry: this.isFutureEntryOnlyMode || isForcedFutureEntryMode,
            futureScheduleEntryDays: (this.hasFutureEntryDaysMode && !isForcedFutureEntryMode) ? futureTimeRecordingDays : null,
        };
        try {
            const res = await scheduleStore.addBulkShift(values);
            if (res?.shiftsFailedToCreate?.length) {
                const msg = res.draftShiftRecords?.length
                    ? t('Bulk shifts created, however some shifts failed to create please check')
                    : t('Failed to create shift due to intersections as shown below');
                commonStore.addNotification(msg, null, res.draftShiftRecords?.length ? 'warning' : 'error');
                this.setState({ shiftsFailedToCreate: res.shiftsFailedToCreate, shiftsFailedToCreateMessage: res.shiftsFailedToCreateMessage || null, hasShiftCreated: !!res.draftShiftRecords?.length });
            } else if (res?.draftShiftRecords?.length) {
                commonStore.addNotification(t('Shifts created successfully'), null, 'success');
                this.setState({
                    shiftsCreatedSuccessfully: { shifts: res.draftShiftRecords, usersMap: res.usersMap || {}, shiftTask: res.shiftTask || {} },
                    hasShiftCreated: true,
                });
            } else {
                commonStore.addNotification(t('No shifts were created for the selected days'), null, 'warning');
            }
        } catch (error) {
            commonStore.addNotification(t(error.message || 'Error'), null, 'error');
            console.error(error);
        }
    };

    renderShiftForm() {
        const { t, commonStore, userStore } = this.props;
        const { start_time, end_time, company_shift_id, defaultDays, autoTimesheetDays, futureTimeRecordingDays,
            formMode, entryChoice, isCheckingRoster, publishedTemplates, start_date, end_date } = this.state;
        const dateTimeRules = commonStore.getDateTimeRules();
        const format = dateTimeRules?.time_format || 'hh:mm';

        if (isCheckingRoster) {
            return <LoadingSpinner />;
        }

        if (formMode === null) {
            return null;
        }

        // department member with no roster in the selected period — nothing to create
        if (formMode === 'no_roster') {
            return <div className="text-center text-muted fw-bold mt-4">{t('No roster found for the selected period.')}</div>;
        }

        // 'choice' lets the user pick roster vs future entry; everyone else keeps their resolved formMode
        const isChoiceMode = formMode === 'choice';
        const isDefaultMode = formMode === 'default' || (isChoiceMode && entryChoice === 'default');
        const isRosterMode = formMode === 'roster' || (isChoiceMode && entryChoice === 'roster');
        // "Future Entry" has no normal-shift option - every day picked is a future entry, locked on.
        const isRosterChoiceFutureEntry = isChoiceMode && entryChoice === 'default';
        const rosterAppliesToMember = !!(commonStore?.config?.client?.data?.basicRules?.workPlanSettings?.allow_work_hours_settings)
            && userStore.currentUser.employee_type !== 'no_department';
        // 'default' as a roster-applies member only happens via the no-roster fallback - always forced future
        const isDepartmentFutureEntryOnly = this.hasFutureScheduleEntryPermission && formMode === 'default' && rosterAppliesToMember;
        const isForcedFutureEntryMode = isRosterChoiceFutureEntry || isDepartmentFutureEntryOnly;

        const defaultShiftLabel = this.companyShiftsOptions
            ? (this.companyShiftsOptions.find(s => s.value === company_shift_id)?.label || '')
            : (commonStore.config?.client?.data?.basicRules?.startTimeRules?.name || t('Default Shift'));

        return (
            <>
                {/* let user pick between roster shifts and a future entry */}
                {isChoiceMode && (
                    <Row className="m-0 mt-3">
                        <div className="d-flex gap-2">
                            <Button
                                fill={entryChoice === 'roster'}
                                variant={entryChoice === 'roster' ? undefined : 'outline-secondary'}
                                onClick={() => this.setState({ entryChoice: 'roster' })}
                            >
                                {t('Roster Shift')}
                            </Button>
                            <Button
                                fill={entryChoice === 'default'}
                                variant={entryChoice === 'default' ? undefined : 'outline-secondary'}
                                onClick={() => this.setState({ entryChoice: 'default' })}
                            >
                                {t('Future')} {t('Shift')}
                            </Button>
                        </div>
                    </Row>
                )}

                {/* Default mode only: shift name (disabled), editable times, weekday selection for shift creation */}
                {isDefaultMode && (
                    <>
                        <div className="row m-0 mt-3">
                            <div className="col-md-4">
                                <div className="form-group field field-string">
                                    <label className="control-label fs15">{t('Working Shift')}</label>
                                    {this.companyShiftsOptions ? (
                                        <Select
                                            options={this.companyShiftsOptions}
                                            value={this.companyShiftsOptions.find(s => s.value === company_shift_id)}
                                            isDisabled
                                            style={{ width: '100%' }}
                                        />
                                    ) : (
                                        <input className="form-control" value={defaultShiftLabel} disabled />
                                    )}
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-group field field-string">
                                    <label className="control-label fs15">{t('From')}</label>
                                    <TimePickerInput
                                        value={minutesToTimeInput(start_time, false)}
                                        format={format === 'hh:mm' ? 'HH:mm' : format}
                                        onChange={value => this.setState({ start_time: getMinutesFromTimeInput(value) })}
                                        className="worklog-input"
                                    />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-group field field-string">
                                    <label className="control-label fs15">{t('To')}</label>
                                    <TimePickerInput
                                        value={minutesToTimeInput(end_time, false)}
                                        format={format === 'hh:mm' ? 'HH:mm' : format}
                                        onChange={value => this.setState({ end_time: getMinutesFromTimeInput(value) })}
                                        className="worklog-input"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row m-0 mt-3">
                            <div className="d-flex flex-wrap">
                                <label className="control-label fs15 mt-1">{t('Weekdays')}<RequiredStar /></label>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip id="tooltip-weekdays">{t('Please select the weekdays for which you want to create shifts')}.</Tooltip>}
                                >
                                    <i className="fa fa-info-circle text-muted mt-1 mx-2" style={{ opacity: '0.5' }} aria-hidden="true" />
                                </OverlayTrigger>
                                {defaultDays.map((item, index) => (
                                    <CheckboxButton
                                        key={item.day}
                                        isChecked={item.checked}
                                        onChange={evt => this.handleCheck(index, evt)}
                                        label={t(item.day)}
                                        isRightPadding={10}
                                        isSatSun={index === 5 || index === 6}
                                        className={index === 7 ? '' : 'overflow-property'}
                                    />
                                ))}
                            </div>
                        </div>
                    </>
                )}

                {(this.isFutureEntryOnlyMode || isForcedFutureEntryMode) &&
                    <div className="row m-0 mt-3">
                        <div className="d-flex align-items-center">
                            <Checkbox
                                isChecked={true}
                                disabled={true}
                                label={t('Future Time Recording')}
                                wrapperClassName="fs15"
                            />
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip id="tooltip-future-time-recording" className="text-start">
                                        {t(`Schedule time recording in advance. When the scheduled date arrives, a timesheet will be
                                            generated automatically based on the scheduled shift time, even when Auto Time Recording
                                            is disabled in the user’s profile`)}.
                                    </Tooltip>
                                }
                            >
                                <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true" />
                            </OverlayTrigger>
                        </div>
                    </div>
                }

                {/* pick which weekdays become future entries; hidden when every day already is one */}
                {this.hasFutureEntryDaysMode && isDefaultMode && !isForcedFutureEntryMode &&
                    <div className="row m-0 mt-2">
                        <div className="d-flex flex-wrap">
                            <AutoTimeRecordingDays
                                autoTimesheetDays={futureTimeRecordingDays}
                                onChange={(days) => this.setState({ futureTimeRecordingDays: days })}
                                disabledDayKeys={AUTO_TIMESHEET_DAY_KEYS.filter((k, i) => !defaultDays[i]?.checked)}
                                label="Future Time Recording"
                                tooltipText={`Schedule time recording in advance. When the scheduled date arrives, a timesheet will be
                                    generated automatically based on the scheduled shift time, even when Auto Time Recording
                                    is disabled in the user’s profile`}
                                labelClassName="auto-timesheet-days-label"
                                isFutureEntryPicker
                            />
                        </div>
                    </div>
                }

                <div className="row m-0 mt-2">
                    <div className="d-flex flex-wrap">
                        <AutoTimeRecordingDays
                            autoTimesheetDays={autoTimesheetDays}
                            onChange={(days) => this.setState({ autoTimesheetDays: days })}
                            disabledDayKeys={
                                this.isOwnAutoTimesheetProfileDisabled
                                    ? AUTO_TIMESHEET_DAY_KEYS
                                    : (isDefaultMode && !this.isFutureEntryOnlyMode
                                        ? AUTO_TIMESHEET_DAY_KEYS.filter((k, i) => !defaultDays[i]?.checked)
                                        : [])
                            }
                            forcedOnDayKeys={
                                (this.isFutureEntryOnlyMode || isForcedFutureEntryMode) && isDefaultMode
                                    ? AUTO_TIMESHEET_DAY_KEYS.filter((k, i) => defaultDays[i]?.checked)
                                    : (this.hasFutureEntryDaysMode && isDefaultMode
                                        ? AUTO_TIMESHEET_DAY_KEYS.filter((k, i) => defaultDays[i]?.checked && futureTimeRecordingDays && futureTimeRecordingDays[k] === true)
                                        : [])
                            }
                            labelClassName="auto-timesheet-days-label"
                        />
                    </div>
                </div>

                {/* Project + Task — both paths */}
                <div className="row m-0 mt-3">
                    <div className="col-md-6">
                        <div className="form-group field field-string">
                            <label className="control-label">{t('Project')} <RequiredStar /></label>
                            <br />
                            <ProjectsWidget
                                value={this.state.project_id}
                                onChange={(e) => this.setState({ project_id: e, task_id: null })}
                                applyOnFirstLoad={false}
                                fromShift={false}
                                maxMenuHeight={150}
                            />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group field field-string">
                            <label className="control-label">{t('Task')} <RequiredStar /></label>
                            <br />
                            <TasksWidget
                                className="form-control"
                                onChange={(value) => this.setState({ task_id: value })}
                                value={this.state.task_id}
                                defaultIfOne
                                defaultByGeneric
                                filterByProject={this.state.project_id}
                                maxMenuHeight={150}
                            />
                        </div>
                    </div>
                </div>

                {/* Roster mode only: roster scheduler */}
                {isRosterMode && (
                    <RosterSchedulePreview
                        publishedTemplates={publishedTemplates}
                        start_date={start_date}
                        end_date={end_date}
                        currentUser={userStore.currentUser}
                        dateTimeRules={dateTimeRules}
                        t={t}
                    />
                )}
            </>
        );
    }

    render() {
        const { t, commonStore } = this.props;
        const { start_date, end_date, formMode, entryChoice, activeDatePicker, shiftsFailedToCreate, shiftsFailedToCreateMessage, shiftsCreatedSuccessfully } = this.state;
        const dateTimeRules = commonStore.getDateTimeRules();
        // Save only makes sense when there is actually a shift to create (default profile shift, roster shifts, or a resolved choice between the two)
        const showSaveButton = (formMode === 'default' || formMode === 'roster' || formMode === 'choice') && !this.state.isCheckingRoster;
        const isRosterSave = formMode === 'roster' || (formMode === 'choice' && entryChoice === 'roster');

        return (
            <Modal dialogClassName="lg bulk-shift-modal" className="lg" size="lg" show onHide={() => this.props.finish(this.state.hasShiftCreated)}>
                <Modal.Header className="set_ModalHeader">
                    <Modal.Title className="w-100 text-center custom-modal-title">
                        {t(this.isFutureEntryOnlyMode ? 'Create Future Entry' : 'Create Shifts')}
                    </Modal.Title>
                    <img
                        src={CloseModalIcon}
                        alt="closeIcon"
                        className="cursor-pointer"
                        onClick={() => this.props.finish(this.state.hasShiftCreated)}
                    />
                </Modal.Header>

                <Modal.Body style={{ minHeight: '400px' }}>
                    {shiftsCreatedSuccessfully ? (
                        <BulkShiftSuccessSummary
                            data={shiftsCreatedSuccessfully}
                            dateTimeRules={dateTimeRules}
                            onClose={() => this.props.finish(true)}
                            t={t}
                        />
                    ) : !shiftsFailedToCreate ? (
                        <div className="row">
                            {/* Step 1 — always shown: date range */}
                            <div className="row m-0">
                                <div className="col-xs-6 col-md-6">
                                    <div className="form-group field field-string">
                                        <label className="control-label">{t('From date')} <RequiredStar /></label>
                                        <br />
                                        <DatePicker
                                            selected={new Date(start_date)}
                                            open={activeDatePicker === 'from'}
                                            onInputClick={() => this.setState({ activeDatePicker: activeDatePicker === 'from' ? null : 'from' })}
                                            onClickOutside={() => this.setState({ activeDatePicker: null })}
                                            onChange={(date) => this.handleDateChange('start_date', date)}
                                            dateFormat={longDateFormat(dateTimeRules)}
                                            minDate={new Date().setDate(new Date().getDate() + 1)}
                                            maxDate={new Date().setMonth(new Date().getMonth() + 5)}
                                        />
                                    </div>
                                </div>
                                <div className="col-xs-6 col-md-6">
                                    <div className="form-group field field-string">
                                        <label className="control-label">{t('To date')} <RequiredStar /></label>
                                        <br />
                                        <DatePicker
                                            selected={new Date(end_date)}
                                            open={activeDatePicker === 'to'}
                                            onInputClick={() => this.setState({ activeDatePicker: activeDatePicker === 'to' ? null : 'to' })}
                                            onClickOutside={() => this.setState({ activeDatePicker: null })}
                                            onChange={(date) => this.handleDateChange('end_date', date)}
                                            dateFormat={longDateFormat(dateTimeRules)}
                                            minDate={new Date().setDate(new Date().getDate() + 1)}
                                            maxDate={new Date().setMonth(new Date().getMonth() + 5)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Step 2 — shown after dates selected */}
                            {this.renderShiftForm()}
                        </div>
                    ) : (
                        <div className="mt-3">
                            <AfterBulkCreateFailedResponseModalBody
                                shiftsFailedToCreate={shiftsFailedToCreate}
                                message={shiftsFailedToCreateMessage}
                                dateTimeRules={dateTimeRules}
                                t={t}
                            />
                        </div>
                    )}
                </Modal.Body>

                {!shiftsFailedToCreate && !shiftsCreatedSuccessfully && showSaveButton && (
                    <Modal.Footer className="border-0">
                        <div className="row m-0 w-100 mb-4">
                            <div className="col-md-6 d-flex">
                                <Button fill wd type="submit" onClick={isRosterSave ? this.handleSaveRoster : this.handleSaveBulk}>
                                    {t('Save')}
                                </Button>
                                <div className="ml-30">
                                    <Button wd type="submit" onClick={() => this.props.finish(this.state.hasShiftCreated)}>
                                        {t('Cancel')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Modal.Footer>
                )}
                {!showSaveButton && !shiftsCreatedSuccessfully && (
                    <Modal.Footer className="border-0">
                        <div className="row m-0 w-100 mb-4">
                            <div className="col-md-6 d-flex">
                                <Button wd type="submit" onClick={() => this.props.finish(this.state.hasShiftCreated)}>
                                    {t('Cancel')}
                                </Button>
                            </div>
                        </div>
                    </Modal.Footer>
                )}
            </Modal>
        );
    }
}

export default inject('userStore', 'commonStore', 'scheduleStore')(
    applicationRouter(withLocalization(observer(MemberBulkShiftForm)))
);
